import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth, db } from "@/lib/firebase";
import {
  arrayUnion,
  collection,
  addDoc,
  doc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import getApp from "@/lib/firebase";
import { useAuthContext } from "@/contexts/AuthContext";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { ValuePropCarousel } from "@/components/features/ValuePropCarousel";
import { parseInviteInput } from "@/lib/inviteParser";
import { useTranslations } from "@/contexts/I18nContext";
import { userFacingMessage } from "@/lib/errors/userFacingError";
import type { Household } from "@/lib/models";

const PENDING_INVITE_KEY = "pending_invite_code";

/**
 * Onboarding gate — exclusive surface shown when the user is authenticated
 * but has zero households. Parity with iOS `OnboardingGateView`.
 */
function OnboardingGate() {
  const navigate = useNavigate();
  const t = useTranslations();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuthContext();
  const { profile, loading: householdLoading } = useHouseholdContext();

  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [inviteInput, setInviteInput] = useState("");
  const [householdName, setHouseholdName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect unauthenticated users to login.
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login", { replace: true });
    }
  }, [authLoading, user, navigate]);

  // If the user already has a household, bounce to the dashboard — this
  // keeps the gate exclusive to the no-household state.
  useEffect(() => {
    if (!householdLoading && profile?.households && profile.households.length > 0) {
      navigate("/dashboard", { replace: true });
    }
  }, [householdLoading, profile, navigate]);

  // Auto-open the join sheet when arriving with `?join=1` (set by /join
  // route) or with a pending invite code in localStorage.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const fromQuery = searchParams.get("join");
    const storedCode = window.localStorage.getItem(PENDING_INVITE_KEY);
    if (storedCode) {
      setInviteInput(storedCode);
    }
    if (fromQuery === "1" || storedCode) {
      setShowJoin(true);
    }
  }, [searchParams]);

  const handleCreate = useCallback(async () => {
    const trimmed = householdName.trim();
    if (!trimmed || !profile?.uid) return;
    setBusy(true);
    setError(null);
    try {
      const newHousehold: Partial<Household> & Record<string, unknown> = {
        household_label: trimmed,
        household_owner: profile.uid,
        household_members: [profile.uid],
        chores: [],
      };
      const ref = collection(db, "households");
      const docRef = await addDoc(ref, {
        ...newHousehold,
        created_at: Timestamp.now(),
      });
      // Add household to the user's profile. The HouseholdProvider listens
      // on member_profiles, so the gate dismisses as soon as this propagates.
      await updateDoc(doc(db, "member_profiles", profile.uid), {
        households: arrayUnion(docRef.id),
      });
      // Persist selection so HouseholdContext picks the right active one.
      window.localStorage.setItem("zoey_selected_household", docRef.id);
      setHouseholdName("");
      setShowCreate(false);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      console.error("createHousehold failed", err);
      setError(t("onboarding.errors.createFailed"));
    } finally {
      setBusy(false);
    }
  }, [householdName, profile, navigate, t]);

  const handleJoin = useCallback(async () => {
    const parsed = parseInviteInput(inviteInput);
    if (parsed.kind === "invalid") {
      setError(t("onboarding.errors.inviteInvalid"));
      return;
    }
    if (parsed.kind === "hh_id") {
      setError(t("onboarding.errors.inviteOldFormat"));
      return;
    }
    if (!user) return;

    setBusy(true);
    setError(null);
    try {
      const functions = getFunctions(getApp());
      const resolveInvite = httpsCallable<
        { token: string },
        {
          success?: boolean;
          alreadyMember?: boolean;
          householdId?: string;
        }
      >(functions, "resolveInvite");
      const result = await resolveInvite({ token: parsed.token });
      const data = result.data;

      if (data.alreadyMember) {
        setError(t("onboarding.errors.alreadyMember"));
        setBusy(false);
        return;
      }

      if (data.householdId) {
        window.localStorage.setItem("zoey_selected_household", data.householdId);
      }
      window.localStorage.removeItem(PENDING_INVITE_KEY);
      setInviteInput("");
      setShowJoin(false);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(userFacingMessage(err, t("onboarding.errors.joinFailed")));
    } finally {
      setBusy(false);
    }
  }, [inviteInput, user, navigate, t]);

  const handleSignOut = useCallback(async () => {
    try {
      await signOut(auth);
      window.localStorage.removeItem(PENDING_INVITE_KEY);
      navigate("/login", { replace: true });
    } catch (err) {
      console.error("sign out failed", err);
    }
  }, [navigate]);

  if (authLoading || !user || householdLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div
          className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
          style={{
            borderColor: "var(--accent-color, #00C7BE)",
            borderTopColor: "transparent",
          }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col max-w-xl mx-auto px-4 pt-6 pb-8">
      {/* Zoey wordmark */}
      <header className="pb-4">
        <h1
          className="text-3xl font-bold"
          style={{ color: "var(--accent-color, #00C7BE)" }}
        >
          Zoey
        </h1>
      </header>

      {/* Shared value-prop carousel */}
      <div className="flex-1 min-h-[360px]">
        <ValuePropCarousel />
      </div>

      {/* Primary CTAs */}
      <div className="pt-6 space-y-3">
        <button
          type="button"
          onClick={() => {
            setError(null);
            setShowCreate(true);
          }}
          className="w-full py-3 rounded-lg text-white font-semibold transition-opacity"
          style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
        >
          {t("onboarding.createCta")}
        </button>
        <button
          type="button"
          onClick={() => {
            setError(null);
            setShowJoin(true);
          }}
          className="w-full py-3 rounded-lg font-semibold border-2"
          style={{
            color: "var(--accent-color, #00C7BE)",
            borderColor: "var(--accent-color, #00C7BE)",
          }}
        >
          {t("onboarding.joinCta")}
        </button>
      </div>

      {/* Bottom actions */}
      <div className="flex items-center justify-center gap-3 pt-5 text-xs text-gray-500">
        <button
          type="button"
          onClick={handleSignOut}
          className="hover:underline"
        >
          {t("onboarding.signOut")}
        </button>
        <span>·</span>
        <a
          href="https://zoeyorganizer.com/getting-started"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:underline"
        >
          {t("onboarding.help")}
        </a>
      </div>

      {showCreate && (
        <Sheet onClose={() => !busy && setShowCreate(false)} closeLabel={t("common.close")}>
          <h2 className="text-lg font-semibold mb-2">{t("onboarding.create.title")}</h2>
          <p className="text-sm text-gray-500 mb-4">
            {t("onboarding.create.subtitle")}
          </p>
          <input
            type="text"
            value={householdName}
            onChange={(e) => setHouseholdName(e.target.value)}
            placeholder={t("onboarding.create.placeholder")}
            disabled={busy}
            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 mb-3 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color,#00C7BE)]"
          />
          {error && (
            <p className="text-sm text-red-500 mb-3">{error}</p>
          )}
          <button
            type="button"
            onClick={handleCreate}
            disabled={busy || !householdName.trim()}
            className="w-full py-2.5 rounded-lg text-white font-semibold disabled:opacity-50"
            style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
          >
            {busy ? t("onboarding.create.busy") : t("onboarding.create.submit")}
          </button>
        </Sheet>
      )}

      {showJoin && (
        <Sheet onClose={() => !busy && setShowJoin(false)} closeLabel={t("common.close")}>
          <h2 className="text-lg font-semibold mb-2">{t("onboarding.join.title")}</h2>
          <p className="text-sm text-gray-500 mb-4">
            {t("onboarding.join.subtitle")}
          </p>
          <input
            type="text"
            value={inviteInput}
            onChange={(e) => setInviteInput(e.target.value)}
            placeholder={t("onboarding.join.placeholder")}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            disabled={busy}
            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 mb-3 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color,#00C7BE)]"
          />
          {error && (
            <p className="text-sm text-red-500 mb-3">{error}</p>
          )}
          <button
            type="button"
            onClick={handleJoin}
            disabled={busy || !inviteInput.trim()}
            className="w-full py-2.5 rounded-lg text-white font-semibold disabled:opacity-50"
            style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
          >
            {busy ? t("onboarding.join.busy") : t("onboarding.join.submit")}
          </button>
        </Sheet>
      )}
    </div>
  );
}

function Sheet({
  children,
  onClose,
  closeLabel,
}: {
  children: React.ReactNode;
  onClose: () => void;
  closeLabel: string;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
      <button
        type="button"
        aria-label={closeLabel}
        onClick={onClose}
        className="absolute inset-0 cursor-default"
      />
      <div className="relative w-full sm:max-w-md bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl p-6 shadow-lg">
        {children}
      </div>
    </div>
  );
}

export function OnboardingPage() {
  return <OnboardingGate />;
}
